// src/pages/MentoringInvitePage.js

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/** ---- CSV Preview Helpers (clientseitig, nur für .csv) ---- **/

const REQUIRED_COLS = ['email', 'role', 'kunden_id', 'projekt_id'];
const OPTIONAL_COLS = ['language'];

function stripBOM(s = '') {
  if (!s) return s;
  // Entfernt UTF-8 BOM (\uFEFF) am Anfang, falls vorhanden
  return s.replace(/^\uFEFF/, '');
}

function pickDelimiter(headerLine) {
  // Wähle das häufigere Trennzeichen in der Kopfzeile
  const commas = (headerLine.match(/,/g) || []).length;
  const semis = (headerLine.match(/;/g) || []).length;
  return semis > commas ? ';' : ',';
}

function splitCsvLine(line, delimiter) {
  // Splittet anhand delimiter, aber ignoriert delimiter innerhalb von Anführungszeichen
  // Beispiel-RegEx: /(,|;)(?=(?:[^"]*"[^"]*")*[^"]*$)/
  const re = new RegExp(`${delimiter}(?=(?:[^"]*"[^"]*")*[^"]*$)`, 'g');
  const parts = line.split(re);
  return parts.map((p) => {
    let cell = p.trim();
    if (cell.startsWith('"') && cell.endsWith('"')) {
      cell = cell.slice(1, -1).replace(/""/g, '"');
    }
    return cell;
  });
}

function analyzeCsvText(textRaw) {
  try {
    const text = stripBOM(textRaw);
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length === 0) {
      return { kind: 'csv', ok: false, error: 'Empty file' };
    }
    const first = lines[0];
    const delimiter = pickDelimiter(first);
    const headers = splitCsvLine(first, delimiter).map((h) =>
      h.trim().toLowerCase()
    );

    const presentRequired = REQUIRED_COLS.filter((c) => headers.includes(c));
    const missingRequired = REQUIRED_COLS.filter((c) => !headers.includes(c));
    const presentOptional = OPTIONAL_COLS.filter((c) => headers.includes(c));

    return {
      kind: 'csv',
      ok: true,
      delimiter,
      headers,
      presentRequired,
      missingRequired,
      presentOptional,
      rowsDetected: Math.max(0, lines.length - 1),
    };
  } catch (e) {
    return { kind: 'csv', ok: false, error: e?.message || 'Parse error' };
  }
}

/** ---- Template Text Mode Helpers ---- **/

function decodeHtmlEntities(s = '') {
  if (!s) return '';
  return s
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function htmlToPlainText(html = '') {
  if (!html) return '';
  let s = String(html);

  // Zeilenumbrüche / Absätze
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/p\s*>/gi, '\n\n');
  s = s.replace(/<\/div\s*>/gi, '\n');

  // Tags entfernen (öffnet nur Textmodus)
  s = s.replace(/<[^>]+>/g, '');

  // Entities decoden
  s = decodeHtmlEntities(s);

  // Mehrfach-Newlines glätten
  s = s.replace(/\r/g, '');
  s = s.replace(/\n{3,}/g, '\n\n').trim();

  return s;
}

function escapeHtml(s = '') {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function plainTextToHtml(text = '') {
  const raw = String(text || '').replace(/\r/g, '').trim();

  const wrapperStart =
    '<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.5;color:#111;">';
  const wrapperEnd = '</div>';

  if (!raw) {
    return `${wrapperStart}<p></p>${wrapperEnd}`;
  }

  // Absätze: leere Zeile trennt Paragraphen
  const paragraphs = raw.split(/\n\s*\n/g);

  const htmlParagraphs = paragraphs
    .map((p) => {
      const lines = p.split('\n');

      // Jede Zeile escapen, dann innerhalb Absatz mit <br/> verbinden
      const escaped = lines.map((ln) => escapeHtml(ln)).join('<br/>');

      return `<p>${escaped}</p>`;
    })
    .join('\n');

  return `${wrapperStart}\n${htmlParagraphs}\n${wrapperEnd}`;
}

/** ---- Hauptseite ---- **/

function MentoringInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [surveyId, setSurveyId] = useState('');
  // Manuelle Kontakte inkl. Sprache
  const [contacts, setContacts] = useState([
    { email: '', role: 'mentee', language: 'auto' },
  ]);

  // Upload-Zustände
  const [file, setFile] = useState(null);
  const [uploadDefaultLang, setUploadDefaultLang] = useState('auto'); // optional: Default fürs Upload
  const [csvInfo, setCsvInfo] = useState(null); // Ergebnis der clientseitigen CSV-Analyse

  // Meldungen
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  // Template-Modal
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);

  // Survey-ID aus URL ziehen
  useEffect(() => {
    const idFromUrl = searchParams.get('survey_id');
    if (idFromUrl) setSurveyId(idFromUrl);
  }, [searchParams]);

  function handleAddRow() {
    setContacts((prev) => [
      ...prev,
      { email: '', role: 'mentee', language: 'auto' },
    ]);
  }

  function handleChange(index, field, value) {
    const updated = [...contacts];
    updated[index][field] = value;
    setContacts(updated);
  }

  function handleRemove(index) {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  }

  // Sprache normalisieren: 'de' | 'en' | 'auto'
  function normalizeInviteLang(x) {
    if (!x) return 'auto';
    const v = String(x).trim().toLowerCase();
    return v === 'de' || v === 'en' || v === 'auto' ? v : 'auto';
  }

  async function handleSubmit() {
    setMessage('');
    setError('');

    try {
      // Kontakte bereinigen
      const normalizedContacts = contacts.map((c) => ({
        email: (c.email || '').trim().toLowerCase(),
        role: (c.role || 'mentee').trim(),
        language: normalizeInviteLang(c.language),
      }));

      const body = {
        survey_id: surveyId,
        contacts: normalizedContacts,
      };

      const res = await axios.post('/mentoring/invite', body, {
        withCredentials: true,
      });
      setMessage(res.data.message || t('sendSuccess', 'Invitations sent'));
    } catch (err) {
      console.error('Fehler bei /mentoring/invite:', err);
      setError(
        err.response?.data?.error ||
          t('inviteError', 'Sending invitations failed')
      );
    }
  }

  function handleBackToHr() {
    navigate('/hr');
  }

  function handleOpenEmailTemplateModal() {
    setShowEmailTemplateModal(true);
  }

  function handleCloseEmailTemplateModal() {
    setShowEmailTemplateModal(false);
  }

  // Datei wählen → wenn CSV, clientseitige Analyse anzeigen
  function handleFileChange(e) {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setMessage('');
    setError('');
    setCsvInfo(null);

    if (!f) return;

    const name = f.name.toLowerCase();
    if (name.endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = String(ev.target?.result || '');
        const info = analyzeCsvText(text);
        setCsvInfo(info);
      };
      reader.onerror = () => {
        setCsvInfo({ kind: 'csv', ok: false, error: 'Read error' });
      };
      reader.readAsText(f);
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      setCsvInfo({
        kind: 'excel',
        ok: true,
        note: 'Excel preview is not supported client-side; columns will be validated on the server.',
      });
    } else {
      setCsvInfo({
        kind: 'other',
        ok: false,
        error: 'Unsupported file type',
      });
    }
  }

  // Beispiel-CSV bereitstellen
  function downloadSampleCsv() {
    const header = 'email,role,kunden_id,projekt_id,language\n';
    const row1 = 'klaus@example.com,mentor,14,21,de\n';
    const row2 = 'sara@example.com,mentee,14,21,en\n';
    const blob = new Blob([header + row1 + row2], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mentoring-invite-sample.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function handleFileUpload() {
    if (!file) {
      setError(t('fileNotSelected', 'No file selected'));
      return;
    }
    setMessage('');
    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('survey_id', surveyId);
      // Optional: Default-Sprache fürs Backend (kann ignoriert werden, falls nicht implementiert)
      formData.append('default_language', normalizeInviteLang(uploadDefaultLang));

      const res = await axios.post('/mentoring/invite/upload', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage(
        res.data.message || t('fileSuccess', 'File uploaded successfully')
      );
    } catch (err) {
      console.error('Fehler bei /mentoring/invite/upload:', err);
      setError(err.response?.data?.error || t('uploadError', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="flex justify-start mb-6">
        <button
          onClick={handleBackToHr}
          className="flex items-center gap-2 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          {t('backToDashboard', 'Back to dashboard')}
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-6">
        {t('headline', 'Invite contacts')}
      </h2>

      {message && <p className="text-green-400 mb-4">{message}</p>}
      {error && <p className="text-red-400 mb-4">{error}</p>}

      <div className="bg-gray-800 p-6 rounded-2xl shadow-md mb-8">
        <p>
          <strong>{t('currentSurvey', 'Current survey')}</strong>{' '}
          {surveyId || t('noSurvey', 'No survey selected')}
        </p>
        <p className="mt-2">
          {t('intro', 'Add contacts manually or upload a CSV/Excel file.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Manuelle Eintragung */}
        <div className="bg-gray-800 p-6 rounded-2xl shadow-md">
          <h3 className="text-xl font-bold mb-4">
            {t('manualContacts', 'Manual contacts')}
          </h3>

          {contacts.map((c, i) => (
            <div key={i} className="flex items-center gap-4 mb-4">
              <input
                type="email"
                value={c.email}
                onChange={(e) => handleChange(i, 'email', e.target.value)}
                placeholder={t('emailPlaceholder', 'email@example.com')}
                className="flex-1 p-2 bg-gray-700 rounded text-white"
                aria-label={t('email', 'Email')}
              />

              <select
                value={c.role}
                onChange={(e) => handleChange(i, 'role', e.target.value)}
                className="p-2 bg-gray-700 rounded text-white"
                title={t('role', 'Role')}
                aria-label={t('role', 'Role')}
              >
                <option value="mentee">{t('roleMentee', 'Mentee')}</option>
                <option value="mentor">{t('roleMentor', 'Mentor')}</option>
                <option value="fuehrungskraft">
                  {t('roleLeader', 'Leader')}
                </option>
              </select>

              {/* Sprache pro Kontakt */}
              <select
                value={c.language || 'auto'}
                onChange={(e) => handleChange(i, 'language', e.target.value)}
                className="p-2 bg-gray-700 rounded text-white"
                title={t('language', 'Language')}
                aria-label={t('language', 'Language')}
              >
                <option value="auto">{t('lang.auto', 'Auto')}</option>
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>

              <button
                onClick={() => handleRemove(i)}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                title={t('remove', 'Remove')}
                aria-label={t('remove', 'Remove')}
              >
                X
              </button>
            </div>
          ))}

          <div className="flex gap-4 mt-4">
            <button
              onClick={handleAddRow}
              className="bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-4 rounded-2xl transition"
            >
              {t('addRow', 'Add row')}
            </button>
            <button
              onClick={handleSubmit}
              className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-2xl transition"
            >
              {t('sendInvites', 'Send invites')}
            </button>
          </div>
        </div>

        {/* Datei-Upload */}
        <div className="bg-gray-800 p-6 rounded-2xl shadow-md">
          <h3 className="text-xl font-bold mb-4">
            {t('csvHeadline', 'Upload CSV/Excel')}
          </h3>

          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-gray-300">
              {t(
                'csvHint',
                'Required columns: email, role, kunden_id, projekt_id. Optional: language.'
              )}
            </span>
            <button
              onClick={downloadSampleCsv}
              type="button"
              className="text-sm underline text-mentixxgreen hover:text-green-300"
            >
              {t('downloadSample', 'Download sample CSV')}
            </button>
          </div>

          {/* Default language fürs Upload (falls CSV ohne language-Spalte) */}
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-gray-300">
              {t('defaultUploadLanguage', 'Default language for upload')}:
            </label>
            <select
              value={uploadDefaultLang}
              onChange={(e) => setUploadDefaultLang(e.target.value)}
              className="p-2 bg-gray-700 rounded text-white"
              aria-label={t(
                'defaultUploadLanguage',
                'Default language for upload'
              )}
              title={t('defaultUploadLanguage', 'Default language for upload')}
            >
              <option value="auto">{t('lang.auto', 'Auto')}</option>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>

          <input
            type="file"
            onChange={handleFileChange}
            accept=".csv,.xls,.xlsx"
            className="mb-3"
            aria-label={t('chooseFile', 'Choose file')}
          />

          {/* CSV/Excel Analyse & Badges */}
          {csvInfo && csvInfo.kind === 'csv' && (
            <div className="mb-4 text-sm">
              {!csvInfo.ok ? (
                <p className="text-red-400">
                  {t('csvPreviewError', 'CSV preview error')}: {csvInfo.error}
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-gray-300">
                      {t('detectedColumns', 'Detected columns')}:
                    </span>
                    {REQUIRED_COLS.map((c) => {
                      const ok = csvInfo.presentRequired.includes(c);
                      return (
                        <span
                          key={c}
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            ok
                              ? 'bg-green-700 text-white'
                              : 'bg-red-700 text-white'
                          }`}
                          title={
                            ok ? t('present', 'present') : t('missing', 'missing')
                          }
                        >
                          {c} {ok ? '✓' : '✗'}
                        </span>
                      );
                    })}
                    {OPTIONAL_COLS.map((c) => {
                      const ok = csvInfo.presentOptional.includes(c);
                      return (
                        <span
                          key={c}
                          className={`px-2 py-0.5 rounded-full text-xs ${
                            ok
                              ? 'bg-green-700 text-white'
                              : 'bg-gray-600 text-white'
                          }`}
                          title={
                            ok
                              ? t('presentOptional', 'optional present')
                              : t('optional', 'optional')
                          }
                        >
                          {c} {ok ? '✓' : ''}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-gray-300">
                    {t('rowsDetected', '{{count}} data rows detected', {
                      count: csvInfo.rowsDetected || 0,
                    })}
                    {csvInfo.missingRequired?.length
                      ? ` • ${t('missingCols', 'Missing')}: ${csvInfo.missingRequired.join(
                          ', '
                        )}`
                      : ''}
                  </p>
                </>
              )}
            </div>
          )}

          {csvInfo && csvInfo.kind === 'excel' && (
            <p className="text-sm text-gray-300 mb-4">
              {t(
                'excelInfo',
                'Excel preview is not supported client-side; columns will be validated on the server.'
              )}
            </p>
          )}

          {file && (
            <p className="text-green-400 mb-4">
              {t('fileChosen', 'Selected file:')} <strong>{file.name}</strong>
            </p>
          )}

          <button
            onClick={handleFileUpload}
            disabled={!file || uploading}
            className="bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-4 rounded-2xl transition disabled:opacity-60"
          >
            {uploading
              ? t('uploading', 'Uploading…')
              : t('uploadFile', 'Upload file')}
          </button>
        </div>
      </div>

      {/* E-Mail-Template bearbeiten */}
      <div className="mt-10">
        <button
          onClick={handleOpenEmailTemplateModal}
          className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-6 rounded-2xl transition"
        >
          {t('adjustMail', 'Adjust email template')}
        </button>
      </div>

      {showEmailTemplateModal && (
        <EmailTemplateModal onClose={handleCloseEmailTemplateModal} />
      )}
    </div>
  );
}

/** ---- E-Mail-Template Modal ---- **/

function EmailTemplateModal({ onClose }) {
  const { t, i18n } = useTranslation();
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [templateLang, setTemplateLang] = useState('de');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // In der Praxis aus Session/Context holen; hier statisch für Demo
  const tenantId = 12;

  // Default Template-Sprache initial aus i18n ableiten (nur de/en)
  useEffect(() => {
    const lng = (i18n.language || '').toLowerCase();
    setTemplateLang(lng.startsWith('en') ? 'en' : 'de');
  }, [i18n.language]);

  function getDefaultTemplateForLang(lng) {
    const isEn = lng === 'en';

    const defaultSubject = isEn ? 'Your invitation to MENVIO' : 'Deine Einladung zu MENVIO';

    const defaultBody = isEn
      ? [
          'Hi,',
          '',
          "you've been invited to join the mentoring program on MENVIO.",
          '',
          'Click the button below to register:',
          '{{BUTTON}}',
          '',
          'If the button does not work, use this link:',
          '{{LINK}}',
          '',
          'This invitation link is valid until {{VALID_UNTIL_LOCAL}}.',
          '',
          'Best regards,',
          'Your MENVIO Team',
        ].join('\n')
      : [
          'Hallo,',
          '',
          'du wurdest zum Mentoring-Programm auf MENVIO eingeladen.',
          '',
          'Klicke auf den Button, um dich zu registrieren:',
          '{{BUTTON}}',
          '',
          'Falls der Button nicht funktioniert, nutze diesen Link:',
          '{{LINK}}',
          '',
          'Dieser Einladungslink ist gültig bis {{VALID_UNTIL_LOCAL}}.',
          '',
          'Viele Grüße',
          'Dein MENVIO Team',
        ].join('\n');

    return { defaultSubject, defaultBody };
  }

  // Template für selected language laden
  useEffect(() => {
    let cancelled = false;

    setError('');
    setMessage('');

    const { defaultSubject, defaultBody } = getDefaultTemplateForLang(templateLang);

    axios
      .get(`/mentoring/email-templates/${tenantId}?lang=${templateLang}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;

        const subj = res.data.subject || defaultSubject;
        const htmlBody = res.data.body || '';

        setSubject(subj);
        setBodyText(htmlBody ? htmlToPlainText(htmlBody) : defaultBody);
      })
      .catch((err) => {
        if (cancelled) return;

        if (err.response?.status === 404) {
          // Keine Vorlage gefunden → Defaults anzeigen (und später speicherbar)
          setSubject(defaultSubject);
          setBodyText(defaultBody);
          return;
        }

        setError(err.response?.data?.error || t('loadError', 'Loading failed'));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateLang, tenantId, t]);

  async function handleSave() {
    setError('');
    setMessage('');

    try {
      // Textmodus: Klartext → HTML für DB/Senden
      const bodyHtml = plainTextToHtml(bodyText);

      await axios.post(
        '/mentoring/email-templates',
        {
          tenant_id: tenantId,
          lang: templateLang,
          subject,
          body: bodyHtml,
        },
        { withCredentials: true }
      );

      setMessage(t('saveSuccess', 'Template saved'));
    } catch (err) {
      setError(err.response?.data?.error || t('saveError', 'Saving failed'));
    }
  }

  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div className="bg-white p-6 rounded-2xl w-full max-w-md text-black">
        <h3 className="text-xl font-bold mb-4">
          {t('emailTemplateTitle', 'Email template')}
        </h3>

        {error && <p className="text-red-600 mb-2">{error}</p>}
        {message && <p className="text-green-600 mb-2">{message}</p>}

        <label className="block mb-2 font-semibold">
          {t('language', 'Language')}
        </label>
        <select
          value={templateLang}
          onChange={(e) => setTemplateLang(e.target.value)}
          className="w-full mb-4 p-2 bg-gray-200 rounded"
          aria-label={t('language', 'Language')}
        >
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>

        <label className="block mb-2 font-semibold">
          {t('subject', 'Subject')}
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full mb-4 p-2 bg-gray-200 rounded"
        />

        <label className="block mb-2 font-semibold">{t('body', 'Body')}</label>

        {/* Textmodus: nur Klartext editieren */}
        <textarea
          rows={10}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          className="w-full p-2 bg-gray-200 rounded"
        />

        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={handleSave}
            className="bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-4 rounded-2xl"
          >
            {t('save', 'Save')}
          </button>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-400 text-white font-bold py-2 px-4 rounded-2xl"
          >
            {t('cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MentoringInvitePage;
